function getResponseContentType(response) {
  return (
    response?.headers?.["content-type"] ||
    response?.headers?.["Content-Type"] ||
    ""
  ).toLowerCase();
}

function isHttpUrl(value = "") {
  return /^https?:\/\//i.test(String(value).trim());
}

function isPdfContentType(contentType = "") {
  const type = String(contentType).toLowerCase();
  return type.includes("application/pdf") || type.includes("octet-stream");
}

async function readBlobErrorMessage(blob) {
  const text = await blob.text();
  let message = "Unable to download file.";

  try {
    const json = JSON.parse(text);
    message =
      json.message ||
      json.Message ||
      json.error ||
      json.Error ||
      message;
  } catch {
    if (text.trim()) {
      message = text.slice(0, 200);
    }
  }

  return message;
}

export function assertPdfApiSuccess(payload) {
  if (!payload || typeof payload !== "object") return;

  const status = payload?.status ?? payload?.Status;
  if (status && String(status).toLowerCase() === "error") {
    throw new Error(
      payload?.message ||
        payload?.Message ||
        "Unable to download PDF."
    );
  }
}

export async function validateBlobDownloadResponse(response) {
  if (!response?.data) {
    throw new Error("No file received.");
  }

  const contentType = getResponseContentType(response);

  if (contentType.includes("json") || contentType.includes("text/html")) {
    throw new Error(await readBlobErrorMessage(response.data));
  }

  if (response.data instanceof Blob && response.data.size === 0) {
    throw new Error("Empty file received.");
  }

  return response;
}

export async function parseBlobRequestError(error) {
  const blob = error?.response?.data;

  if (blob instanceof Blob) {
    return readBlobErrorMessage(blob);
  }

  if (blob instanceof ArrayBuffer) {
    try {
      const json = JSON.parse(new TextDecoder().decode(blob));
      return json.message || json.Message || "Unable to download file.";
    } catch {
      return "Unable to download file.";
    }
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.message ||
    "Unable to download file."
  );
}

export async function downloadPdfFromUrl(url, filename = "download.pdf") {
  if (!url || typeof window === "undefined") {
    throw new Error("PDF URL is unavailable.");
  }

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error("Unable to fetch PDF.");
    }

    const blob = await response.blob();

    if (!blob.size) {
      throw new Error("Empty file received.");
    }

    triggerBlobDownload(
      {
        data: blob,
        headers: { "content-type": blob.type || "application/pdf" },
      },
      filename
    );
  } catch {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

function collectPdfUrlFromObject(source = {}) {
  return (
    source.inclusionPdf ||
    source.InclusionPdf ||
    source.invoicePdf ||
    source.InvoicePdf ||
    source.voucherPdf ||
    source.VoucherPdf ||
    source.voucherUrl ||
    source.VoucherUrl ||
    source.invoiceUrl ||
    source.InvoiceUrl ||
    source.downloadLink ||
    source.DownloadLink ||
    source.pdfLink ||
    source.PdfLink ||
    source.fileUrl ||
    source.FileUrl ||
    source.pdfUrl ||
    source.PdfUrl ||
    source.url ||
    source.Url ||
    ""
  );
}

export function extractPdfUrl(payload) {
  if (!payload) return "";

  if (typeof payload === "string") {
    return isHttpUrl(payload) ? payload.trim() : "";
  }

  const raw = payload?.data ?? payload;

  if (typeof raw === "string" && isHttpUrl(raw)) {
    return raw.trim();
  }

  const candidates = [];

  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    candidates.push(raw);
    if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
      candidates.push(raw.data);
    }
  }

  for (const source of candidates) {
    const url = collectPdfUrlFromObject(source);
    if (url) return url;
  }

  return "";
}

export const extractItineraryPdfUrl = extractPdfUrl;

function parseJsonPayload(data) {
  if (typeof data !== "string") return data;

  if (isHttpUrl(data)) {
    return { __directUrl: data.trim() };
  }

  try {
    return JSON.parse(data);
  } catch {
    throw new Error("Invalid PDF response.");
  }
}

export function normalizePdfDownloadResponse(axiosResponse) {
  const contentType = getResponseContentType(axiosResponse);
  const rawData = axiosResponse?.data;

  if (
    rawData instanceof Blob ||
    (rawData instanceof ArrayBuffer && isPdfContentType(contentType))
  ) {
    const blob =
      rawData instanceof Blob
        ? rawData
        : new Blob([rawData], { type: contentType || "application/pdf" });

    return { kind: "blob", blob };
  }

  let payload = rawData;

  if (payload instanceof ArrayBuffer) {
    payload = parseJsonPayload(new TextDecoder().decode(payload));
  } else if (typeof payload === "string") {
    payload = parseJsonPayload(payload);
  }

  if (payload?.__directUrl) {
    return { kind: "url", url: payload.__directUrl };
  }

  assertPdfApiSuccess(payload);

  const pdfUrl = extractPdfUrl(payload);
  if (pdfUrl) {
    return { kind: "url", url: pdfUrl };
  }

  return { kind: "json", payload };
}

export async function downloadPdfFromApiResponse(response, filename) {
  if (response?.kind === "blob") {
    if (!response.blob?.size) {
      throw new Error("Empty file received.");
    }

    triggerBlobDownload(
      {
        data: response.blob,
        headers: { "content-type": response.blob.type || "application/pdf" },
      },
      filename
    );
    return;
  }

  if (response?.kind === "url") {
    await downloadPdfFromUrl(response.url, filename);
    return;
  }

  const payload = response?.kind === "json" ? response.payload : response;

  assertPdfApiSuccess(payload);

  const pdfUrl = extractPdfUrl(payload);
  if (!pdfUrl) {
    throw new Error("PDF is unavailable.");
  }

  await downloadPdfFromUrl(pdfUrl, filename);
}

export function triggerBlobDownload(response, filename = "download.pdf") {
  if (typeof window === "undefined" || !response?.data) return;

  const contentType =
    getResponseContentType(response) || "application/pdf";

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
